import { useCallback, useEffect, useState } from 'react';
import useS3Upload from '#hooks/use-upload.js';
import { checkImageFormat, newId } from '#helpers/helpers.js';

function generateMediaIndex(media) {
    if (!media) return;
    media.id = media.id ?? newId();
    return media;
}

function parseInitialData(initialData) {
    if (!Array.isArray(initialData) || !initialData.length) return [];
    return initialData.filter(data => data).map(generateMediaIndex);
}

export function useMediaList({
    initialData,
    onAdd,
    onAddImage,
    onUpload,
    onDelete,
    onEdit,
    stateless,
    setState,
    setUnsavedChanges
}) {
    let [media, setMedia] = useState(parseInitialData(stateless ? null : initialData));
    const [isUploading, setIsUploading] = useState(false);
    const setHasUnsavedChanges = typeof setUnsavedChanges === 'function' ? setUnsavedChanges : () => { };

    if (stateless) {
        media = initialData;
        setMedia = (fnOrData) => {
            setState(typeof fnOrData === 'function' ? fnOrData(initialData) : fnOrData);
        };
    }

    useEffect(() => {
        if (!stateless)
            setMedia(() => parseInitialData(initialData));
    }, [initialData, setMedia, stateless]);

    const onUploadComplete = useCallback(
        async (data) => {
            const { file, tempKey } = data;
            setMedia((state) => {
                let media = state.find((el) => el.isUploading && el.lastModified === file.lastModified);
                if (!media) return state;
                const { isUploading, coords, ...rest } = media;
                const newMedia = {
                    ...rest,
                    coords,
                    src: `${MEDIA_URL}/${tempKey}`,
                };
                const newState = state.map(media => media.id === newMedia.id ? newMedia : media);

                if (typeof onUpload === 'function') onUpload(newState);
                return newState;
            });
        },
        [setMedia, onUpload]
    );

    const s3Upload = useS3Upload({
        onSuccess: onUploadComplete,
        onBeforeUpload: (e) => {
            const [file] = e.target.files;
            return checkImageFormat(file);
        }
    });

    const addDocumentAction = useCallback(
        (event, files) => {
            const providedFiles = files ?? event.target.files;
            if (!providedFiles || providedFiles.length === 0) return;

            for (const file of providedFiles) {
                file.id = newId();
                if (event) {
                    const eventFile = Array.from(event.target.files).find(ef => ef.lastModified === file.lastModified);
                    if (!eventFile) continue;
                    s3Upload.props.onChange({ target: { files: [eventFile] } });
                    setMedia((state) => {
                        const newMedia = {
                            id: file.id,
                            lastModified: file.lastModified,
                            name: file.name,
                            sourceType: file.sourceType ?? 'U',
                            src: URL.createObjectURL(eventFile),
                            isUploading: true,
                            coords: file.coords ?? { zoom: 1, x: 0, y: 0 }
                        };
                        const newState = [...state, newMedia];

                        if (typeof onAdd === 'function') onAdd(newState, newMedia, file);

                        return newState;
                    });
                }
            }
            setHasUnsavedChanges(true);
        },
        [setMedia, onAdd, s3Upload]
    );

    const addImageAction = useCallback(
        async (toUploadFiles = [], files) => {
            if (!files || files.length === 0) return;
            const newState = []
            setIsUploading(true);
            for (const file of files) {
                // se il file è già presente su s3 non lo carico
                let isUploading = false;
                // caso in cui il file sia caricato da locale
                const toUploadFile = toUploadFiles?.find(ef => ef.lastModified === file.lastModified);
                if (toUploadFile) {
                    isUploading = true;
                    s3Upload.props.onChange({ target: { files: [toUploadFile] } });
                }

                const newMedia = {
                    id: file.id || newId(),
                    name: file.name ?? 'External',
                    src: file.tempSrc ?? file.src,
                    coords: file.coords ?? { zoom: 1, x: 0, y: 0 },
                    lastModified: file.lastModified,
                    isUploading
                };

                newState.push(newMedia);
            }
            setMedia(newState);
            if (typeof onAddImage === 'function') onAddImage(newState);
            setIsUploading(false);
            setHasUnsavedChanges(true);
        },
        [setMedia, onAddImage]
    );

    const deleteAction = useCallback(
        (id) => {
            setMedia((state) => {
                const newState = [...state.filter((el) => el.id !== id)];

                if (typeof onDelete === 'function') onDelete(newState, id);

                return newState;
            });
            setHasUnsavedChanges(true);
        },
        [setMedia, onDelete]
    );

    const editAction = useCallback(
        (id, field, value) => {
            setMedia((state) => {
                const updated = structuredClone(state);
                const media = updated.find((el) => el.id === id);
                if (!media) return updated;
                media[field] = value;
                if (typeof onEdit === 'function') onEdit(updated);
                return updated;
            });
            setHasUnsavedChanges(true);
        },
        [setMedia, onEdit]
    );

    return {
        data: media,
        onAdd: addDocumentAction,
        onAddImage: addImageAction,
        onDelete: deleteAction,
        onEdit: editAction,
        isUploading
    };
}